import { DatePickerFr } from '../ui/DatePickerFr';
import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { AlertCircle, Edit, Loader2, Plus, Trash2 } from 'lucide-react';
import { formatDate, formatMoney } from './constants';

export default function ModalsAcompteEcheance({
  echeanceDate, editAcompteForm, editPaymentDateForm, getName, handleDeleteAcompte,
  handleEcheance, handleEditAcompte, handleSchedule, handleUpdatePaymentDate, processing,
  scheduleItems, selectedAcompte, selectedAcompteIndex, selectedDoc, setEcheanceDate,
  setEditAcompteForm, setEditPaymentDateForm, setScheduleItems, setShowDeleteAcompteModal,
  setShowEcheanceModal, setShowEditAcompteModal, setShowEditPaymentDateModal, setShowScheduleModal,
  showDeleteAcompteModal, showEcheanceModal, showEditAcompteModal, showEditPaymentDateModal, showScheduleModal,
}) {
  return (
    <>
      {/* Echeance Modal */}
      <Dialog open={showEcheanceModal} onOpenChange={setShowEcheanceModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier l'échéance</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>Nouvelle date</Label>
            <DatePickerFr
              value={echeanceDate}
              onChange={setEcheanceDate}
              className="w-full"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEcheanceModal(false)}>Annuler</Button>
            <Button onClick={handleEcheance} disabled={processing}>
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Modal */}
      <Dialog open={showScheduleModal} onOpenChange={setShowScheduleModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Échéancier personnalisé</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4 max-h-60 overflow-y-auto">
            {scheduleItems.map((item, i) => (
              <div key={i} className="flex gap-2 items-center">
                <DatePickerFr
                  value={item.date}
                  onChange={(v) => {
                    const updated = [...scheduleItems];
                    updated[i].date = v;
                    setScheduleItems(updated);
                  }}
                  className="flex-1"
                />
                <Input
                  type="number"
                  value={item.amount}
                  onChange={(e) => {
                    const updated = [...scheduleItems];
                    updated[i].amount = parseFloat(e.target.value) || 0;
                    setScheduleItems(updated);
                  }}
                  className="w-24"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setScheduleItems(scheduleItems.filter((_, idx) => idx !== i))}
                  disabled={scheduleItems.length <= 1}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              onClick={() => setScheduleItems([...scheduleItems, { date: '', amount: 0 }])}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Ajouter
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScheduleModal(false)}>Annuler</Button>
            <Button onClick={handleSchedule} disabled={processing}>
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Édition Acompte */}
      <Dialog open={showEditAcompteModal} onOpenChange={setShowEditAcompteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-blue-600" />
              Modifier l'acompte
            </DialogTitle>
            <DialogDescription>
              {selectedDoc?.reference} - Acompte n°{(selectedAcompteIndex || 0) + 1}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-gray-500">Montant *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editAcompteForm.montant}
                  onChange={(e) => setEditAcompteForm({ ...editAcompteForm, montant: e.target.value })}
                  className="font-mono"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Date</Label>
                <DatePickerFr
                  value={editAcompteForm.date}
                  onChange={(v) => setEditAcompteForm({ ...editAcompteForm, date: v })}
                  className="w-full"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-gray-500">Mode de paiement</Label>
                <Select value={editAcompteForm.mode} onValueChange={(v) => setEditAcompteForm({ ...editAcompteForm, mode: v })}>
                  <SelectTrigger className="text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="virement">Virement</SelectItem>
                    <SelectItem value="sepa">SEPA</SelectItem>
                    <SelectItem value="carte">CB</SelectItem>
                    <SelectItem value="cheque">Chèque</SelectItem>
                    <SelectItem value="especes">Espèces</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Statut</Label>
                <Select value={editAcompteForm.statut} onValueChange={(v) => setEditAcompteForm({ ...editAcompteForm, statut: v })}>
                  <SelectTrigger className="text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recu">✅ Reçu</SelectItem>
                    <SelectItem value="programme">📅 Programmé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs text-gray-500">Référence paiement</Label>
              <Input
                value={editAcompteForm.reference}
                onChange={(e) => setEditAcompteForm({ ...editAcompteForm, reference: e.target.value })}
                placeholder="Réf. virement, chèque..."
              />
            </div>

            <div>
              <Label className="text-xs text-gray-500">Observations</Label>
              <Input
                value={editAcompteForm.observations}
                onChange={(e) => setEditAcompteForm({ ...editAcompteForm, observations: e.target.value })}
                placeholder="Notes complémentaires..."
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowEditAcompteModal(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleEditAcompte}
              disabled={processing || !editAcompteForm.montant}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Enregistrer les modifications
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Suppression/Annulation Acompte */}
      <Dialog open={showDeleteAcompteModal} onOpenChange={setShowDeleteAcompteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Annuler l'acompte
            </DialogTitle>
            <DialogDescription>
              {selectedDoc?.reference} - Acompte n°{(selectedAcompteIndex || 0) + 1}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-800">
                <strong>⚠️ Attention :</strong> Cette action va annuler l'acompte suivant :
              </p>
              <div className="mt-3 bg-white rounded p-3 border">
                <div className="flex justify-between">
                  <span className="text-gray-600">Montant:</span>
                  <span className="font-bold text-red-700">{formatMoney(selectedAcompte?.montant || 0)}</span>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-gray-600">Date:</span>
                  <span>{formatDate(selectedAcompte?.date)}</span>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-gray-600">Mode:</span>
                  <span>{selectedAcompte?.mode || 'Virement'}</span>
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-600">
              L'acompte sera marqué comme "Annulé" et ne sera plus comptabilisé dans le total des paiements reçus.
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteAcompteModal(false)}>
              Conserver l'acompte
            </Button>
            <Button
              onClick={handleDeleteAcompte}
              disabled={processing}
              className="bg-red-600 hover:bg-red-700"
            >
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirmer l'annulation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* V37.38 — Modal : Rectifier la date de paiement (mise a jour comptable) */}
      <Dialog open={!!showEditPaymentDateModal} onOpenChange={setShowEditPaymentDateModal}>
        <DialogContent className="max-w-md" data-testid="modal-edit-payment-date">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-indigo-600" />
              Modifier la date de paiement
            </DialogTitle>
            <DialogDescription>
              <span className="block">{selectedDoc?.reference} — {getName(selectedDoc)}</span>
              <span className="block mt-1 text-xs text-gray-500">
                Rectifie la date enregistree sur le recu de contribution ESS pour mise a jour comptable.
                Le PDF sera regenere a la volee au prochain telechargement.
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              <strong>Date actuellement enregistree :</strong>{' '}
              {selectedDoc?.paiement?.date_paiement
                ? formatDate(selectedDoc.paiement.date_paiement)
                : 'Non renseignee'}
            </div>

            <div>
              <Label htmlFor="edit-payment-date" className="text-sm font-medium">
                Nouvelle date de paiement <span className="text-red-500">*</span>
              </Label>
              <DatePickerFr
                id="edit-payment-date"
                value={editPaymentDateForm?.date_paiement || ''}
                onChange={(v) =>
                  setEditPaymentDateForm((prev) => ({ ...prev, date_paiement: v }))
                }
                className="w-full"
              />
            </div>

            <div>
              <Label htmlFor="edit-payment-motif" className="text-sm font-medium">
                Motif (optionnel — pour l&apos;audit)
              </Label>
              <Textarea
                id="edit-payment-motif"
                data-testid="input-edit-payment-motif"
                placeholder="Ex : Reception du justificatif bancaire en differe — date reelle du virement."
                value={editPaymentDateForm?.motif || ''}
                onChange={(e) =>
                  setEditPaymentDateForm((prev) => ({ ...prev, motif: e.target.value }))
                }
                rows={2}
              />
            </div>

            <p className="text-[11px] text-gray-500 italic">
              Cette modification est tracee dans le journal d&apos;audit (utilisateur, ancienne et nouvelle date, motif).
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowEditPaymentDateModal(false)}
              data-testid="btn-cancel-edit-payment-date"
            >
              Annuler
            </Button>
            <Button
              onClick={handleUpdatePaymentDate}
              disabled={processing || !editPaymentDateForm?.date_paiement}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="btn-confirm-edit-payment-date"
            >
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Enregistrer la nouvelle date
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
